import { SlideStatement } from "../../../SlideStatement";

const TILES: Array<{
  icon: string[];
  label: string;
  win: string;
  how: string;
}> = [
  {
    icon: ["m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"],
    label: "AI Discoverability",
    win: "Get your app discovered by users on Claude, ChatGPT, Gemini, and Perplexity",
    how: "AI agents cite the open web, so we audit and act on Reddit, publisher sites and mentions — not just your listing.",
  },
  {
    icon: [
      "M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z",
      "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    ],
    label: "Competitors",
    win: "Monitor, and react to your competitors' marketing moves",
    how: "Every rival's metadata, ranking & asset change, surfaced the day it's live.",
  },
  {
    icon: [
      "M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z",
    ],
    label: "User Sentiment",
    win: "Reviews become a roadmap",
    how: "12 months of reviews and ratings across both stores, trended into themes \u2014 then turned into listing edits and product fixes.",
  },
  {
    icon: [
      "M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418",
    ],
    label: "Localization",
    win: "Unlock the global market for your app",
    how: "Text, keywords and screenshots localized across up to 87 languages, each market audited on its own search demand — and published to both stores in one revision.",
  },
  {
    icon: [
      "M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75",
    ],
    label: "Store Algorithms",
    win: "React faster to ranking shifts",
    how: "Thousands of keywords per store tracked 12× a day against your competitors — then listings re-tuned to how each store actually indexes title, subtitle and keyword field.",
  },
];

export default function TheCustomerWin() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Customer's Win">
        <div>
          <h1 className="text-balance text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Activate Apptonomy.
            <br />
            Rank higher. Convert better.
          </h1>
          {/* Restates the claim's scope rather than supporting it, so it sits
              above the rule (see slide 3). */}
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            On every app in your portfolio, every week — consistently
            outperform your competition.
          </p>
        </div>
      </SlideStatement>

      {/* Tiles fill the middle band so there's no dead space. Six columns
          carry a 3 + 2 layout: the first three tiles span 2 each, the last two
          span 3 each, so both rows run full width (TPF, 2026-09-08). */}
      <div className="grid grid-cols-1 gap-3 py-6 md:grid-cols-6 md:gap-4 md:py-0 md:pt-6 md:min-h-0 md:flex-1 md:content-center">
        {TILES.map((tile, i) => (
          <div
            key={tile.label}
            className={`flex flex-col rounded-2xl bg-white p-5 text-left shadow-sm ring-1 ring-slate-200 md:p-5 ${
              i < 3 ? "md:col-span-2" : "md:col-span-3"
            }`}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 md:h-12 md:w-12">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                aria-hidden="true"
                className="h-6 w-6"
              >
                {tile.icon.map((d) => (
                  <path
                    key={d}
                    d={d}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>
            </span>
            <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-600 md:text-xs">
              {tile.label}
            </p>
            <p className="mt-1.5 text-base font-bold leading-tight tracking-tight text-slate-900 md:text-lg">
              {tile.win}
            </p>
            <p className="mt-2 text-xs leading-snug text-slate-600 md:text-sm">
              {tile.how}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}


